import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild
} from "@angular/core";
import {DynForm} from "../../domain/meta/dyn-form";
import {FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from "@angular/forms";
import {FormService} from "../../services/form.service";
import {Observable} from "rxjs/Observable";
import {DynSelectFieldOptions} from "../../domain/meta/fields/select/dyn-select-field-options";
import {DynFieldType} from "../../domain/meta/dyn-field-type";
import {FormUtils} from "../../domain/meta/form-utils";
import {clone} from "../../../core/commons/utils/clone";
import {MatTabChangeEvent} from "@angular/material";
import {GlobalAppService} from "../../../core/commons/global-app.service";
import {Store} from "@ngrx/store";
import * as formActions from "../../state/form-editor.actions";
import {ActivatedRoute, Router} from "@angular/router";
import {AppState} from "../../../reducers/index";
import {MessageSeverity} from "../../../core/commons/message-severity";
import "rxjs/add/operator/distinctUntilChanged";
import "rxjs/add/operator/take";
import "rxjs/add/operator/finally";
import {DynFormData} from "../../domain/data/dyn-form-data";
import {DynField} from "../../domain/meta/dyn-field";
import {DynSelectField} from "../../domain/meta/fields/select/dyn-select-field";
import {DynSubformField} from "../../domain/meta/fields/dyn-subform-field";
import {DynTextField} from "../../domain/meta/fields/dyn-text-field";
import {Subscription} from "rxjs/Subscription";
import {Ministry} from "app/profile/domain/ministry/ministry";
import {MinistryService} from "app/profile/service/ministry.service";
import {tap} from "rxjs/operators";
import {Location} from "@angular/common";
import {MatDialog} from "@angular/material/dialog";
import {ImagePickerDialog} from "../image-picker-dialog/image-picker-dialog.component";
import {FormImage} from "../../domain/meta/form-image";
import { CookieConsent } from "app/core/commons/cookie-consent.js/cookie-consent";

@Component({
    selector: "app-form-editor",
    templateUrl: "./form-editor.component.html",
    styleUrls: ["./form-editor.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormEditorComponent implements OnInit, OnDestroy {
    fieldTypes: Observable<DynFieldType[]>;
    optionsList: Observable<DynSelectFieldOptions<any>[]>; // = Observable.of([]);
    subformList: Observable<DynForm[]>; // = Observable.of([]);
    newVersion = false;

    form: Observable<DynForm>;
    _formData: Observable<DynFormData>;
    private subscriptions = [];
    private _formFormGroup: FormGroup;
    ministry: Ministry = null;
    formImage: FormImage;
    // Ministries
    public ministries: Ministry[];
    filteredOptions: Ministry[];

    @ViewChild("inputFocusFillable") textareas: ElementRef;

    public editSubform: Boolean = false;
    private cookieConsent: CookieConsent;

    constructor(
        private appService: GlobalAppService,
        private fb: FormBuilder,
        private formsService: FormService,
        private store: Store<AppState>,
        private route: ActivatedRoute,
        private router: Router,
        private cd: ChangeDetectorRef,
        private ministryService: MinistryService,
        private _location: Location,
        public dialog: MatDialog,
    ) {
        this.cookieConsent = new CookieConsent();
        this.editSubform = this.cookieConsent.getCookie('subform-edit') == 'true';
    }

    get formFormGroup(): FormGroup {
        return this._formFormGroup;
    }

    addImage() {
        let dialogRef = this.dialog.open(ImagePickerDialog, {
            width: '650px',
            data: {
                image: this.formImage
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                if (result.data != null) {
                    this.formImage = result;
                } else {
                    this.formImage = null;
                }
                this.cd.markForCheck();
            }
        });
    }

    get valid(): Observable<boolean> {
        // return this._valid;
        return this.store.select("forms", "formEditor", "formStatus");
    }

    get defaultFormFormGroup(): FormGroup {
        const fg = this.fb.group({
            name: ["Formulario sin titulo", Validators.required],
            description: "",
            subform: false,
            referenceNumber: "",
            ministry: new FormControl(null),
            fields: this.fb.array([])
        });

        return fg;
    }

    get defaultFieldFormGroup(): FormGroup {
        const fg = this.fb.group({
            label: ["", Validators.required],
            type: [0, Validators.required],
            required: [true, Validators.required],
            min: [0, Validators.compose([Validators.min(0)])],
            max: [200, Validators.compose([Validators.min(0), Validators.max(200)])],
            options: [{value: 0, disabled: true}, Validators.required],
            subform: [{value: 0, disabled: true}, Validators.required],
            text: [{value: ""}],
            defaultValue: [{value: ""}],
            alignment: [{value: "LEFT"}],
        });

        this.subscriptions.push(
            fg.controls.type.valueChanges
                .distinctUntilChanged()
                .subscribe(t => this.updateFieldType(fg))
        );
        return fg;
    }

    get fieldsFormArray(): FormArray {
        return this._formFormGroup.get("fields") as FormArray;
    }

    ngOnInit() {
        this.ministryService.getAll().subscribe(ministries => {
            this.ministries = ministries;
            this.filteredOptions = this.ministries;
            //console.log(this.ministries);
        });


        this.fieldTypes = FormService.getDynFieldTypes();
        this.optionsList = this.formsService.options;
        this.subformList = this.formsService.subforms;


        this.subscriptions.push(
            this.route.params
                .switchMap(params => {
                    if (params["id"]) {
                        return this.formsService.get(+params["id"]);
                    } else if (params["template"]) {
                        return this.formsService.get(+params["template"]).map(form => {
                            form.setId(null);
                            form.setName("Copia de " + form.getName());
                            form.getFields().forEach(field => field.setId(null));
                            return form;
                        });
                    } else if (params["version"]) {
                        this.newVersion = true;
                        return this.formsService.get(+params["version"]).map(form => {
                            form.setId(null);
                            form.setState("CREATED");
                            form.getFields().forEach(field => field.setId(null));
                            return form;
                        });
                    } else {
                        return Observable.of(new DynForm());
                    }
                })
                .do((form: DynForm) => {
                    this.formImage = form.image;
                })
                .combineLatest(this.fieldTypes)
                .map(arr => this.buildFormGroup(arr[0], arr[1]))
                .do(fg => (this._formFormGroup = fg))
                // .do(fg => { if (this.newVersion) { fg.controls.name.disable()}})ad
                .subscribe(() => {
                    this.formFormGroup.valueChanges
                        .map(() => this.formFormGroup.getRawValue())
                        //.do(console.log)
                        .combineLatest(this.fieldTypes, this.prepare)
                        .combineLatest(
                            this.formFormGroup.statusChanges
                                // .do(() => console.log(this.formFormGroup))
                                // .do(console.log)
                                // .do(() => console.log(this.getFormValidationErrors(this.formFormGroup)))
                                .map(
                                    status =>
                                        status === "VALID" && this.fieldsFormArray.length > 0
                                )
                        )
                        .subscribe(arr => {
                                //console.log('arr:', arr)
                                this.store.dispatch(
                                    new formActions.Update({value: arr[0], status: arr[1]})
                                )
                            }
                        );
                    this.formFormGroup.updateValueAndValidity();

                    for (const fieldFormGroup of this.fieldsFormArray
                        .controls as FormGroup[]) {
                        if ((fieldFormGroup.controls.type.value as number) === 7 || (fieldFormGroup.controls.type.value as number) === 8) {
                            this.manageStaticTextLabel(fieldFormGroup);
                        }
                    }

                    this.cd.markForCheck();
                })
        );

        this.form = this.store
            .select("forms", "formEditor", "form")
            .filter(form => form !== null)
            .do(() => this.cd.markForCheck());

        this._formData = this.form
            .map(form => DynFormData.createEmpty(form))
            .do(() => this.cd.markForCheck());
    }

    get formData(): Observable<DynFormData> {
        return this._formData.pipe(tap(f => f.getForm().image = this.formImage))
    }

    getFormValidationErrors(form: FormGroup) {
        Object.keys(form.controls).forEach(key => {
            if (key === "fields") {
                const fields: FormArray = form.get(key) as FormArray;
                for (let i = 1; i < fields.length; i++) {
                    this.getFormValidationErrors(fields.at(i) as FormGroup);
                }
            } else {
                const controlErrors: ValidationErrors = form.get(key).errors;
                if (controlErrors != null) {
                    Object.keys(controlErrors).forEach(keyError => {
                        console.log(
                            "Key control: " +
                            key +
                            ", keyError: " +
                            keyError +
                            ", err value: ",
                            controlErrors[keyError]
                        );
                    });
                }
            }
        });
    }

    public buildFormGroup(form: DynForm, types: DynFieldType[]): FormGroup {
        //console.log(form);
        // console.log(types);
        const fg = this.fb.group({
            id: [form.getId()],
            name: this.newVersion
                ? [{value: form.getName(), disabled: true}, Validators.required]
                : [form.getName(), Validators.required],
            version: form.getVersion(),
            description: form.getDescription(),
            referenceNumber: form.getReferenceNumber(),
            subform: form.getSubform(),
            ministry: form.getMinistry(),
            fields: this.fb.array(
                form.getFields().map(field => this.buildFieldFormGroup(field, types))
            )
        });
        /*
            form.getFields().forEach(field => {
                console.log(field);
                (fg.get('fields') as FormArray).push(this.buildFieldFormGroup(field, types))
            });
            */
        return fg;
    }

    compareMinistry(p1: Ministry, p2: Ministry): boolean {
        return p1.equals(p2);
    }

    public buildFieldFormGroup(
        field: DynField,
        types: DynFieldType[]
    ): FormGroup {
        const fg = this.fb.group({
            id: [field.getId()],
            label: [field.getLabel(), Validators.required],
            type: [
                types.find(type => type.fieldTypeName === field.getType()).id,
                Validators.required
            ],
            required: field.getRequired(),
            min: [field.getMin(), Validators.compose([Validators.min(0)])],
            max: [
                field.getMax(),
                Validators.compose([Validators.min(0), Validators.max(5000)])
            ],
            divider: field.getDivider(),
            options:
                field.getType() === "select"
                    ? [
                        (field as DynSelectField).getOptions().getId(),
                        Validators.required
                    ]
                    : [{value: 0, disabled: true}, Validators.required],
            subform:
                field.getType() === "subform"
                    ? [(field as DynSubformField).getForm().getId(), Validators.required]
                    : [{value: 0, disabled: true}, Validators.required],
            text: [field.getText()],
            defaultValue: [field.getDefaultValue()],
            alignment: [field.getAlignment()],
        });
        this.subscriptions.push(
            fg.controls.type.valueChanges
                .distinctUntilChanged()
                .subscribe(t => this.updateFieldType(fg))
        );
        return fg;
    }

    onTabChange(event: MatTabChangeEvent) {
        // this.formEditorStore.map(f => f.forms.formEditor.form).subscribe(f => console.log(f));
        // this._rebuildForm.next(event.index === 1);
        if (event.index === 1) {
            this.store.dispatch(new formActions.BuildForm());
        }
    }

    onSubmit(event) {
        // event.preventDefault();
        this.store
            .select("forms", "formEditor")
            .filter(editor => editor.updated === false)
            .take(1)
            .subscribe(() => this.store.dispatch(new formActions.BuildForm()));
        this.subscriptions.push(
            this.store
                .select("forms", "formEditor")
                .filter(editor => editor.form !== null && editor.updated === true)
                .map(e => e.form)
                .take(1)
                .do(() => this.appService.setLoading(true))
                .map(form => {
                    if (this.formImage) {
                        form.image = this.formImage;
                    }
                    return this.modFillableText(form);
                })
                .switchMap((form: DynForm) => {
                    const fillables = form.getFields().filter(f => f.getType() === "fillableText");
                    if(fillables.some(f => f.getText().split('{@}').length === 1)){
                        throw {fillableError: true};
                    }
                    return this.newVersion
                        ? this.formsService.createNewVersion(form)
                        : this.formsService.save(form)
                    }
                )
                .subscribe(
                    () => {
                        this.appService.sendMsg(
                            MessageSeverity.SUCCESS,
                            "Guardado",
                            "El formulario se ha guardado correctamente."
                        );
                        this.store.dispatch(new formActions.ResetForm());
                        if (this.editSubform) {
                            this.navigateToParent();
                        }else{
                            this.navigateToList();
                        }
                        this.appService.setLoading(false);
                    },
                    e => {
                        if(e.fillableError){
                            this.appService.sendMsg(
                                MessageSeverity.ERROR,
                                "Error",
                                "Error campos de texto completable sin campos para completar"
                            );
                            this.appService.setLoading(false);

                        } else {
                            this.appService.sendMsg(
                                MessageSeverity.ERROR,
                                "Error",
                                "Error al guardar el formulario. " + e.json().message
                            );
                            this.appService.setLoading(false);
                        }

                    }
                )
        );
        // event.preventDefault();
    }

    modFillableText(form: DynForm): DynForm {
        for (var i = 0; i < form.getFields().length; i++) {
            if (form.getFields()[i].getType() == ("fillableText")) {
                var dato = form.getFields()[i].getText();
                form.getFields()[i].setText((dato.split('<CAMPO COMPLETABLE>')).join('{@}'));
            }
        }
        return form;
    }

    createFormFormGroup() {
        this._formFormGroup = this.defaultFormFormGroup;
        //this.filteredOptions = this.getValueChange(this._formFormGroup.controls["ministry"].value);
    }

    search(query: string) {
        //console.log('query', query);
        let result = this.select(query);
        this.filteredOptions = result;
    }

    select(query: string): Ministry[] {
        return this._filter(query);
    }

    private _filter(value: string): Ministry[] {
        const filterValue = value.toLowerCase();

        return this.ministries.filter(option =>
            option.name.toLowerCase().includes(filterValue)
        );
    }

    addFieldFormGroup() {
        this.fieldTypes.subscribe(types =>
            this.fieldsFormArray.push(
                this.buildFieldFormGroup(new DynTextField(), types)
            )
        );
        //console.log(this.fieldsFormArray);
        this.formFormGroup.updateValueAndValidity();
        this.cd.markForCheck();
    }

    deleteDivider(i: number) {
        const f = this.fieldsFormArray.controls[i] as FormGroup;
        //console.log(f.get("divider").value);
        f.get("divider").setValue(false);
        //console.log(f.get("divider").value);
        this.form.subscribe((f: DynForm) => {
            f.getFields()[i].setDivider(false);
            this.store.dispatch(
                new formActions.Update({value: f, status: true})
            )
        });
        this.formFormGroup.updateValueAndValidity();
        this.cd.markForCheck();
    }

    addDivider(i: number) {
        //this.fieldsFormArray.controls[i].value.divider = true;
        const f = this.fieldsFormArray.controls[i] as FormGroup;
        f.get('divider').setValue(true);
        this.form.subscribe((f: DynForm) => {
            f.getFields()[i].setDivider(true);
            this.store.dispatch(
                new formActions.Update({value: f, status: true})
            )
        });

        // console.log("f=");
        //console.log(f);
        this.formFormGroup.updateValueAndValidity();
        this.cd.markForCheck();

    }

    isLastField(i: number): boolean {
        return (this.fieldsFormArray.length - 1 == i) && !this.fieldsFormArray.controls[i].value.divider;
    }

    hasDivider(field): boolean {
        return field.value.divider;
    }

    removeFieldFormGroup(i: number) {
        this.fieldsFormArray.removeAt(i);
        this.cd.markForCheck();
    }

    moveUpFieldFormGroup(i: number) {
        if (i > 0) {
            const f = this.fieldsFormArray.controls[i] as FormGroup;
            this.fieldsFormArray.removeAt(i);
            this.fieldsFormArray.insert(i - 1, f);
        }
        this.cd.markForCheck();
    }

    moveDownFieldFormGroup(i: number) {
        if (i < this.fieldsFormArray.length - 1) {
            const f = this.fieldsFormArray.controls[i] as FormGroup;
            this.fieldsFormArray.removeAt(i);
            this.fieldsFormArray.insert(i + 1, f);
        }
        this.cd.markForCheck();
    }

    moveArrayElement(array: Array<any>, old_index, new_index) {
        if (new_index >= array.length) {
            let k = new_index - array.length;
            while (k-- + 1) {
                array.push(undefined);
            }
        }
        array.splice(new_index, 0, array.splice(old_index, 1)[0]);
        return array;
    }

    updateFieldType(fieldFormGroup: FormGroup) {
        // const fieldFormGroup = this.fieldsFormArray.at(fieldIndex);
        const typeId = fieldFormGroup.controls.type.value as number;
        switch (typeId) {
            case 3: // Lista Desplegable
                fieldFormGroup.controls.options.enable();
                fieldFormGroup.controls.subform.disable();
                break;
            case 6: // Sub Formulario
                fieldFormGroup.controls.subform.enable();
                fieldFormGroup.controls.options.disable();
                break;
            default:
                fieldFormGroup.controls.options.disable();
                fieldFormGroup.controls.subform.disable();
        }
        switch (typeId) {
            case 0: // Texto Corto
            case 1: // Cuadro de Texto
                fieldFormGroup.controls.min.enable();
                fieldFormGroup.controls.max.enable();
                break;
            default:
                fieldFormGroup.controls.min.disable();
                fieldFormGroup.controls.max.disable();
        }
        switch (typeId) {
            case 0:
                fieldFormGroup.controls.min.setValue(0);
                fieldFormGroup.controls.max.setValue(200);
                fieldFormGroup.controls.max.setValidators(
                    Validators.compose([Validators.min(0), Validators.max(200)])
                );
                fieldFormGroup.controls.max.updateValueAndValidity();
                break;
            case 1:
                fieldFormGroup.controls.min.setValue(0);
                fieldFormGroup.controls.max.setValue(5000);
                fieldFormGroup.controls.max.setValidators(
                    Validators.compose([Validators.min(0), Validators.max(5000)])
                );
                fieldFormGroup.controls.max.updateValueAndValidity();
                break;
            default:
                fieldFormGroup.controls.min.setValue(null);
                fieldFormGroup.controls.max.setValue(null);
        }

        if (typeId === 7 || typeId === 8) {
            // texto estatico o texto completable
            fieldFormGroup.controls.text.setValidators([Validators.required]);
            fieldFormGroup.controls.required.setValue(false);
            this.manageStaticTextLabel(fieldFormGroup);
        } else {
            fieldFormGroup.controls.text.clearValidators();
        }

        if (typeId === 9) {
            fieldFormGroup.controls.required.setValue(false);
            if ((fieldFormGroup.controls.label.value as string).toLowerCase() === "sin nombre") {
                fieldFormGroup.controls.label.setValue("Firma");
            }
        }

        fieldFormGroup.controls.text.updateValueAndValidity()
        fieldFormGroup.controls.defaultValue.updateValueAndValidity()
        fieldFormGroup.controls.alignment.updateValueAndValidity()
    }

    // Label del field en base al contenido. Se usan los primeros 15 caracteres.
    private manageStaticTextLabel(formGroup: FormGroup) {
        const labelLength = 15;
        formGroup.controls.text.valueChanges.subscribe(text => {
            let name: string;
            if (text) {
                if (text.length > labelLength) {
                    name = (text as string).substring(0, labelLength).trim() + "...";
                } else {
                    name = text;
                }
            } else {
                name = "Sin nombre";
            }
            //console.warn(name);
            formGroup.controls.label.setValue(name.replace(/{@}/g, "[campo a completar]"));
            this.cd.markForCheck();
        });
    }

    getFieldType(id: number): Observable<DynFieldType> {
        return this.fieldTypes.map(types => types.find(t => t.id === id));
    }

    getFieldAlign(id: string) {
        const alignments = [
            {id: 'LEFT', name: 'Izquierda'},
            {id: 'RIGHT', name: 'Derecha'},
            {id: 'CENTER', name: 'Centrado'},
            {id: 'JUSTIFIED', name: 'Justificado'}
        ];
        return alignments.find(align => align.id === id);
    }

    navigateToList() {
        this.router.navigate(["home", "forms"]);
    }

    navigateToParent() {
        this.router.navigate(['home', 'forms', 'data', this.cookieConsent.getCookie('subform-edit-parent')]);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
        this.store.dispatch(new formActions.ResetForm());
    }

    prepare(value: any, types: Array<DynFieldType>): any {
        value = clone(value, false);
        const names: string[] = [];
        value.fields.forEach((field, index) => {
            field.name = FormUtils.labelToName(field.label);
            // @ts-ignore
            while (names.includes(field.name)) {
                field.name += "~";
            }
            names.push(field.name);
            field.type = types.find(type => type.id === field.type).fieldTypeName;
            if (field.type === "select") {
                field.options = {id: field.options};
            } else if (field.type === "subform") {
                field.form = {id: field.subform};
            }
            field.order = index;
        });
        return value;
    }

    /*
      Métodos para filtrar por ministerios.
    */
    private getAllMinistries(): Observable<Ministry[]> {
        return Observable.of(this.ministries);
    }

    displayFn(ministry?: Ministry): string | undefined {
        return ministry ? ministry.name : undefined;
    }


    applyFilterAndReset() {
        let ministryValue = this._formFormGroup.controls["ministry"].value.id;
    }

    addCampo(i: number) {
        const f = this.fieldsFormArray.controls[i] as FormGroup;
        console.log(f.get("text").value);
        var dato = "<CAMPO COMPLETABLE>";
        if (f.get("text").value == null) {
            f.get("text").setValue(dato);
        } else
            f.get("text").setValue(f.get("text").value + dato);
        this.textareas.nativeElement.focus();
    }

    goBack() {
        this._location.back();
    }

    goBackParent() {
        this._location.back();
        this.router.navigate(['home', 'forms', 'data', this.cookieConsent.getCookie('subform-edit-parent')]);
    }
}



// WEBPACK FOOTER //
// ./src/app/forms/components/form-editor/form-editor.component.ts